if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';

        /*
         * Find our native Personalisation block.
         * It sits elsewhere inside the same product-info element.
         */
        this.personalisation =
          this.closest('product-info')?.querySelector('.sr-personalisation') ||
          null;

        this.setupPersonalisation();

        this.form.addEventListener(
          'submit',
          this.onSubmitHandler.bind(this)
        );
      }

      setupPersonalisation() {
        if (!this.personalisation) return;

        /*
         * Find the controls.
         *
         * The fallback name selectors mean this works with the
         * version of product-personalisation.liquid currently
         * installed, so you don't need to edit that snippet first.
         */
        const giftToggle =
          this.personalisation.querySelector('[data-sr-gift-toggle]');

        const giftMessage =
          this.personalisation.querySelector('[data-sr-gift-message]') ||
          this.personalisation.querySelector(
            '[name="properties[Gift message]"]'
          );

        const engravingToggle =
          this.personalisation.querySelector(
            '[data-sr-engraving-toggle]'
          );

        const font =
          this.personalisation.querySelector('[data-sr-font]') ||
          this.personalisation.querySelector(
            '[name="properties[Font]"]'
          );

        const engravingText =
          this.personalisation.querySelector(
            '[data-sr-engraving-text]'
          ) ||
          this.personalisation.querySelector(
            '[name="properties[Custom text]"]'
          );

        this.personalisationFields = {
          giftToggle,
          giftMessage,
          engravingToggle,
          font,
          engravingText,
        };

        /*
         * Disconnect these controls from Shopify's ordinary
         * product form.
         *
         * Our code reads their values directly and creates the
         * cart request itself. This also helps prevent the old
         * Infinite Options machinery from processing them while
         * we test the replacement.
         */
        Object.values(this.personalisationFields).forEach((field) => {
          if (!field) return;

          field.removeAttribute('name');
          field.removeAttribute('form');
        });

        /*
         * Dynamic Buy It Now cannot safely use our normal
         * single-product payment-button flow when an add-on is
         * selected, so hide it while personalisation is active.
         */
        [giftToggle, engravingToggle].forEach((toggle) => {
          if (!toggle) return;

          toggle.addEventListener('change', () => {
            this.updateDynamicCheckoutVisibility();
          });
        });

        this.updateDynamicCheckoutVisibility();
      }

      getPersonalisationState() {
        if (!this.personalisation || !this.personalisationFields) {
          return null;
        }

        const fields = this.personalisationFields;

        return {
          giftSelected: fields.giftToggle?.checked === true,

          giftMessage:
            fields.giftMessage?.value?.trim() || '',

          engravingSelected:
            fields.engravingToggle?.checked === true,

          engravingIsFree:
            this.personalisation.dataset.engravingFree === 'true',

          engravingLimit:
            parseInt(
              this.personalisation.dataset.engravingLimit || '0',
              10
            ) || 0,

          font:
            fields.font?.value || '',

          engravingText:
            fields.engravingText?.value?.trim() || '',

          giftWrapVariantId:
            Number(
              this.personalisation.dataset.giftWrapVariantId || 0
            ),

          engravingVariantId:
            Number(
              this.personalisation.dataset.engravingVariantId || 0
            ),
        };
      }

      hasSelectedPersonalisation() {
        const state = this.getPersonalisationState();

        if (!state) return false;

        return (
          state.giftSelected ||
          state.engravingSelected
        );
      }

      updateDynamicCheckoutVisibility() {
        const paymentButton =
          this.querySelector('.shopify-payment-button') ||
          this.querySelector('[data-shopify="payment-button"]');

        if (!paymentButton) return;

        paymentButton.hidden = this.hasSelectedPersonalisation();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (
          this.submitButton.getAttribute('aria-disabled') === 'true'
        ) {
          return;
        }

        const personalisationState =
          this.getPersonalisationState();

        const hasPersonalisation =
          personalisationState &&
          (
            personalisationState.giftSelected ||
            personalisationState.engravingSelected
          );

        /*
         * If our native Personalisation system is active,
         * stop another submit listener from also trying to
         * process this submission.
         */
        if (hasPersonalisation) {
          evt.stopImmediatePropagation();
        }

        this.handleErrorMessage();

        const formData = new FormData(this.form);

        const mainVariantId =
          Number(formData.get('id'));

        const quantity =
          Math.max(
            1,
            parseInt(formData.get('quantity') || '1', 10)
          );

        if (!mainVariantId) {
          this.handleErrorMessage(
            'Please select an available product option.'
          );
          return;
        }

        /*
         * Validate required linked add-on products before
         * starting the request.
         */
        if (
          hasPersonalisation &&
          personalisationState.giftSelected &&
          !personalisationState.giftWrapVariantId
        ) {
          this.handleErrorMessage(
            'Gift wrap is temporarily unavailable.'
          );
          return;
        }

        if (
          hasPersonalisation &&
          personalisationState.engravingSelected &&
          !personalisationState.engravingIsFree &&
          !personalisationState.engravingVariantId
        ) {
          this.handleErrorMessage(
            'Engraving is temporarily unavailable.'
          );
          return;
        }

        this.submitButton.setAttribute(
          'aria-disabled',
          true
        );

        this.submitButton.classList.add('loading');

        const spinner =
          this.querySelector('.loading__spinner');

        if (spinner) {
          spinner.classList.remove('hidden');
        }

        const config = fetchConfig('javascript');

        config.headers['X-Requested-With'] =
          'XMLHttpRequest';

        let requestProductVariantId =
          mainVariantId;

        /*
         * =============================================
         * NATIVE PERSONALISATION REQUEST
         * =============================================
         *
         * Shopify receives:
         *
         * Main handbag
         *   ├── Gift Wrap child line, if selected
         *   └── Engraving child line, if paid
         *
         * Free engraving stays as properties on the
         * handbag without a charge product.
         */
        if (
          hasPersonalisation &&
          this.personalisation
        ) {
          const mainProperties = {};

          /*
           * Gift wrap properties shown on the handbag.
           */
          if (personalisationState.giftSelected) {
            mainProperties['Gift wrap'] =
              'Add gift wrap (additional charge)';

            if (personalisationState.giftMessage) {
              mainProperties['Gift message'] =
                personalisationState.giftMessage;
            }
          }

          /*
           * Engraving properties shown on the handbag.
           */
          if (
            personalisationState.engravingSelected
          ) {
            mainProperties['Engraving?'] =
              personalisationState.engravingIsFree
                ? 'Yes please'
                : 'Yes please (additional charge)';

            if (personalisationState.font) {
              mainProperties['Font'] =
                personalisationState.font;
            }

            if (
              personalisationState.engravingText
            ) {
              const engravingLabel =
                personalisationState.engravingLimit > 0
                  ? `Custom text (${personalisationState.engravingLimit} characters max, standard letters and numbers only. Contact us for special characters or emojis)`
                  : 'Custom text';

              mainProperties[engravingLabel] =
                personalisationState.engravingText;
            }
          }

          const mainItem = {
            id: mainVariantId,
            quantity,
            properties: mainProperties,
          };

          /*
           * Preserve a selling plan if one ever exists
           * on the main product.
           */
          const sellingPlan =
            formData.get('selling_plan');

          if (sellingPlan) {
            mainItem.selling_plan =
              Number(sellingPlan);
          }

          const items = [mainItem];

          /*
           * Tuscany Leather Gift Wrap.
           *
           * parent_id attaches the charge product
           * natively to the handbag.
           */
          if (
            personalisationState.giftSelected
          ) {
            items.push({
              id:
                personalisationState
                  .giftWrapVariantId,
              quantity,
              parent_id: mainVariantId,
            });
          }

          /*
           * Paid engraving.
           *
           * Free engraving deliberately adds no
           * surcharge product.
           */
          if (
            personalisationState.engravingSelected &&
            !personalisationState.engravingIsFree
          ) {
            items.push({
              id:
                personalisationState
                  .engravingVariantId,
              quantity,
              parent_id: mainVariantId,
            });
          }

          const body = {
            items,
          };

          /*
           * Ask Shopify to return the freshly rendered
           * cart drawer sections in the same request.
           */
          if (this.cart) {
            body.sections =
              this.cart
                .getSectionsToRender()
                .map((section) => section.id);

            body.sections_url =
              window.location.pathname;

            this.cart.setActiveElement(
              document.activeElement
            );
          }

          config.headers['Content-Type'] =
            'application/json';

          config.body =
            JSON.stringify(body);
        }

        /*
         * =============================================
         * NORMAL PRODUCT REQUEST
         * =============================================
         *
         * If neither Gift Wrap nor Engraving is
         * selected, preserve Studio's original
         * product-form behaviour.
         */
        else {
          if (this.cart) {
            formData.append(
              'sections',
              this.cart
                .getSectionsToRender()
                .map((section) => section.id)
            );

            formData.append(
              'sections_url',
              window.location.pathname
            );

            this.cart.setActiveElement(
              document.activeElement
            );
          }

          delete config.headers['Content-Type'];

          config.body = formData;
        }

        fetch(
          `${routes.cart_add_url}`,
          config
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(
                PUB_SUB_EVENTS.cartError,
                {
                  source: 'product-form',
                  productVariantId:
                    requestProductVariantId,
                  errors:
                    response.errors ||
                    response.description,
                  message:
                    response.message,
                }
              );

              this.handleErrorMessage(
                response.description ||
                response.message ||
                'This item could not be added to your cart.'
              );

              this.error = true;
              return;
            }

            if (!this.cart) {
              window.location =
                window.routes.cart_url;
              return;
            }

            const startMarker =
              CartPerformance.createStartingMarker(
                'add:wait-for-subscribers'
              );

            publish(
              PUB_SUB_EVENTS.cartUpdate,
              {
                source: 'product-form',
                productVariantId:
                  requestProductVariantId,
                cartData: response,
              }
            ).then(() => {
              CartPerformance.measureFromMarker(
                'add:wait-for-subscribers',
                startMarker
              );
            });

            this.error = false;

            const quickAddModal =
              this.closest(
                'quick-add-modal'
              );

            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure(
                      'add:paint-updated-sections',
                      () => {
                        this.cart.renderContents(
                          response
                        );
                      }
                    );
                  });
                },
                { once: true }
              );

              quickAddModal.hide(true);
            } else {
              CartPerformance.measure(
                'add:paint-updated-sections',
                () => {
                  this.cart.renderContents(
                    response
                  );
                }
              );
            }
          })
          .catch((error) => {
            console.error(error);

            this.handleErrorMessage(
              'There was a problem adding this item to your cart. Please try again.'
            );

            this.error = true;
          })
          .finally(() => {
            this.submitButton.classList.remove(
              'loading'
            );

            if (
              this.cart &&
              this.cart.classList.contains(
                'is-empty'
              )
            ) {
              this.cart.classList.remove(
                'is-empty'
              );
            }

            /*
             * Re-enable Add to Cart after the request.
             * The theme's normal variant logic will
             * disable it separately for unavailable
             * variants.
             */
            this.submitButton.removeAttribute(
              'aria-disabled'
            );

            if (spinner) {
              spinner.classList.add('hidden');
            }

            CartPerformance.measureFromEvent(
              'add:user-action',
              evt
            );

            /*
             * Don't let a previous temporary AJAX
             * error leave the form permanently locked.
             */
            this.error = false;
          });
      }

      handleErrorMessage(
        errorMessage = false
      ) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(
            '.product-form__error-message-wrapper'
          );

        if (!this.errorMessageWrapper) {
          return;
        }

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (errorMessage) {
          this.errorMessage.textContent =
            errorMessage;
        }
      }

      toggleSubmitButton(
        disable = true,
        text
      ) {
        if (disable) {
          this.submitButton.setAttribute(
            'disabled',
            'disabled'
          );

          if (text) {
            this.submitButtonText.textContent =
              text;
          }
        } else {
          this.submitButton.removeAttribute(
            'disabled'
          );

          this.submitButtonText.textContent =
            window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector(
          '[name=id]'
        );
      }
    }
  );
}